"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../../../utils/api/auth.js";
import axiosInstance from "../../../../utils/api/axiosInstance.js";
import JsBarcode from "jsbarcode";
import QRCodeLib from "qrcode";
import { generateTicketPDF } from "../../ticketPdf";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import SearchBar from "../../../../components/SearchBar.jsx";
import "../css/TicketsList.css";
import TicketCard from "../components/TicketCard.jsx";
import logoImage from "../../../../assets/BuzzTicketLogo.png";

const DEFAULT_EVENT_IMAGE = "/XPRESS-TICKETS-LOGO2.png";
const eventDetailsCache = {};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const normalizeEventRecords = (events) => {
  if (!Array.isArray(events)) {
    return [];
  }

  return events
    .map((event) => {
      const idCandidates = [
        event.event_id,
        event.eventId,
        event.EventID,
        event.EventId,
        event.id,
        event.eventid,
      ]
        .filter((candidate) => candidate !== undefined && candidate !== null)
        .map((candidate) => String(candidate));

      if (idCandidates.length === 0) {
        return null;
      }

      return {
        ids: idCandidates,
        eventImage:
          event.bannerimage ||
          event.image ||
          event.file_url ||
          event.cover_image_url ||
          event.coverimage ||
          DEFAULT_EVENT_IMAGE,
        background: event.background_images_array || event.background || null,
      };
    })
    .filter(Boolean);
};

const buildEventLookup = (events) => {
  const normalizedEvents = normalizeEventRecords(events);
  return normalizedEvents.reduce((lookup, event) => {
    event.ids.forEach((id) => {
      lookup[id] = event;
    });
    return lookup;
  }, {});
};

const fetchAvailableEventsLookup = async () => {
  try {
    const response = await axiosInstance.get("/shared/events/available");
    return buildEventLookup(response.data);
  } catch (err) {
    console.error("TicketsList - Error fetching available events for lookup:", err);
    return {};
  }
};

const fetchEventDetailsById = async (eventId) => {
  if (!eventId) {
    return null;
  }

  const cacheKey = String(eventId);
  if (Object.prototype.hasOwnProperty.call(eventDetailsCache, cacheKey)) {
    return eventDetailsCache[cacheKey];
  }

  try {
    const response = await axiosInstance.get(`/shared/events/${cacheKey}`);
    const event = response.data;

    if (!event) {
      eventDetailsCache[cacheKey] = null;
      return null;
    }

    const organiserName =
      event.organiser_company ||
      `${event.organiser_firstname || ""} ${event.organiser_surname || ""}`.trim() ||
      event.organizer ||
      "";

    const normalized = {
      eventImage:
        event.bannerimage ||
        event.image ||
        event.file_url ||
        event.cover_image_url ||
        event.coverimage ||
        DEFAULT_EVENT_IMAGE,
      background: event.background_images_array || event.background || null,
      organiserName,
      organiserEmail: event.organiser_email || "",
    };

    eventDetailsCache[cacheKey] = normalized;
    return normalized;
  } catch (err) {
    console.error(`TicketsList - Error fetching event details for ${cacheKey}:`, err);
    eventDetailsCache[cacheKey] = null;
    return null;
  }
};

const TicketsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState({});
  const [generatingTicket, setGeneratingTicket] = useState({});
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [generatingCombinedInvoice, setGeneratingCombinedInvoice] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [ticketNumbers, setTicketNumbers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const processTickets = async () => {
      try {
        setLoading(true);
        setError(null);

        const { purchases, eventInfo } = location.state || {};
        console.log('[TicketsList] 🎫 Processing route state:', {
          purchaseCount: purchases?.length,
          eventInfo: { eventName: eventInfo?.eventName, eventId: eventInfo?.eventId },
        });

        if (!purchases || !Array.isArray(purchases)) {
          setError("No purchase data found");
          return;
        }

        const processedTickets = [];
        const discoveredTicketNumbers = {};

        purchases.forEach((purchase, purchaseIndex) => {
          let delegates = [];

          if (purchase.delegate_details) {
            try {
              let parsedDetails = purchase.delegate_details;
              if (typeof parsedDetails === "string") {
                let cleanedString = parsedDetails
                  .replace(/\\"/g, "\"")
                  .replace(/^"|"$/g, "");

                try {
                  parsedDetails = JSON.parse(cleanedString);
                } catch (e) {
                  const jsonMatch = cleanedString.match(/\[\s*\{.*\}\s*\]|\{.*\}/s);
                  if (jsonMatch) {
                    parsedDetails = JSON.parse(jsonMatch[0]);
                  } else {
                    parsedDetails = [];
                  }
                }
              }

              if (Array.isArray(parsedDetails)) {
                delegates = parsedDetails.filter((d) => d && (d.name || d.email));
              } else if (parsedDetails && typeof parsedDetails === "object") {
                if (parsedDetails.name || parsedDetails.email) {
                  delegates = [parsedDetails];
                } else {
                  delegates = Object.values(parsedDetails).filter(
                    (v) => v && typeof v === "object" && (v.name || v.email),
                  );
                }
              }
            } catch (e) {
              console.error("Error processing delegate_details:", e);
            }
          }

          if (delegates.length === 0) {
            delegates = [
              {
                name: purchase.customerName || purchase.name || purchase.attendeeName || "Guest",
                surname: purchase.customerSurname || purchase.surname || purchase.attendeeSurname || "",
                email: purchase.customerEmail || purchase.email || purchase.attendeeEmail || "",
                phone: purchase.customerPhone || purchase.phone || purchase.attendeePhone || "",
                title: purchase.title || "",
                gender: purchase.gender || "",
                organization: purchase.organization || "",
              },
            ];
          }

          delegates.forEach((delegate, delegateIndex) => {
            if (!purchase) return;

            let ticketPrice = 0;
            if (delegate.amount) {
              ticketPrice = Number.parseFloat(delegate.amount) || 0;
            } else if (purchase.amount) {
              ticketPrice = Number.parseFloat(purchase.amount) || 0;
            } else if (purchase.totalAmount) {
              ticketPrice = Number.parseFloat(purchase.totalAmount) || 0;
            } else if (purchase.price) {
              ticketPrice = Number.parseFloat(purchase.price) || 0;
            }

            const timestamp = Date.now();
            const randomSuffix = Math.floor(Math.random() * 1000);
            const purchaseId = purchase.purchase_id || purchase.id || `temp-${purchaseIndex}`;
            const ticketNumber = `${purchaseId}-${String(delegateIndex + 1).padStart(3, "0")}`;
            const ticketId = `ticket-${ticketNumber}-${timestamp}-${randomSuffix}`;

            if (delegates.length > 1 && purchase.totalAmount) {
              ticketPrice = Number.parseFloat(purchase.totalAmount) / delegates.length || 0;
            }

            const ticket = {
              id: ticketId,
              purchaseId: purchase.id || purchase.purchase_id,
              eventName: purchase.eventName || eventInfo?.eventName || purchase.event_name || "Event",
              eventDate: purchase.eventDate || eventInfo?.eventDate || purchase.event_date,
              eventLocation: purchase.eventLocation || eventInfo?.eventLocation || purchase.event_location,
              eventImage: purchase.eventImage || eventInfo?.eventImage || purchase.event_image,
              ticketType: purchase.ticketType || purchase.package || purchase.ticket_type || "General Admission",
              price: ticketPrice,
              status: purchase.status || purchase.purchase_status || "Active",
              purchaseDate:
                purchase.purchaseDate || purchase.createdAt || purchase.purchase_date || purchase.created_at,
              delegate: {
                name: delegate.name || delegate.firstName || "Guest",
                surname: delegate.surname || delegate.lastName || "",
                email: delegate.email || "No email provided",
                phone: delegate.phone || delegate.phoneNumber || "",
                title: delegate.title || "",
                gender: delegate.gender || "",
              },
              ticketNumber: "",
              qrCode: "",
              barcode: "",
              paystackReference: purchase.paystack_reference || "",
              originalPurchase: purchase,
              originalDelegate: delegate,
            };

            if (purchase.purchase_id && purchase.ticket_number) {
              discoveredTicketNumbers[purchase.purchase_id] = purchase.ticket_number;
            }

            processedTickets.push(ticket);
          });
        });

        let eventLookup = {};
        if (processedTickets.length > 0) {
          eventLookup = await fetchAvailableEventsLookup();
        }

        const ticketsWithImages = await Promise.all(
          processedTickets.map(async (ticket) => {
            const eventId = ticket.originalPurchase.event_id || ticket.originalPurchase.eventId;

            if (eventId) {
              const lookupKey = String(eventId);
              let eventDetails = eventLookup[lookupKey];

              if (!eventDetails) {
                eventDetails = await fetchEventDetailsById(lookupKey);
              }

              if (eventDetails) {
                return {
                  ...ticket,
                  eventImage: eventDetails.eventImage || ticket.eventImage || DEFAULT_EVENT_IMAGE,
                  background: eventDetails.background ?? ticket.background ?? null,
                  organiserName: eventDetails.organiserName || ticket.organiserName || "",
                  organiserEmail: eventDetails.organiserEmail || ticket.organiserEmail || "",
                };
              }
            }

            return {
              ...ticket,
              eventImage: ticket.eventImage || DEFAULT_EVENT_IMAGE,
            };
          }),
        );

        console.log('[TicketsList] ✅ Tickets processed:', {
          columns: 'id, purchaseId, eventName, eventDate, eventLocation, ticketType, price, status, delegate[{name,surname,email,phone,title,gender}], paystackReference',
          priceSource: 'delegate.amount → purchase.amount → purchase.totalAmount → purchase.price',
          count: ticketsWithImages.length,
        });
        setTickets(ticketsWithImages);
        setTicketNumbers((prev) => ({ ...discoveredTicketNumbers, ...prev }));
        setSelectedTicketIds((prev) =>
          prev.filter((id) => ticketsWithImages.some((ticket) => ticket.id === id))
        );
      } catch (err) {
        console.error("Error processing tickets:", err);
        setError(`Failed to process tickets: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    processTickets();
  }, [location.state]);

  const handleBackToReview = () => {
    navigate("/reviewpurchase");
  };

  const getTicketNumberForPurchase = async (purchaseId) => {
    if (!purchaseId) {
      throw new Error("Missing purchase ID for ticket lookup");
    }

    if (ticketNumbers[purchaseId]) {
      return ticketNumbers[purchaseId];
    }

    console.log('[TicketsList] 🔢 Fetching ticket number — API: GET /customer/ticket-number/:purchaseId', { purchaseId });
    const response = await axiosInstance.get(`/customer/ticket-number/${purchaseId}`);
    const ticketNumber = response.data?.ticket_number;
    console.log('[TicketsList] ✅ Ticket number received:', { purchaseId, ticketNumber });

    if (!ticketNumber) {
      throw new Error("ticket_number missing in response");
    }

    setTicketNumbers((prev) => ({ ...prev, [purchaseId]: ticketNumber }));
    return ticketNumber;
  };

  const handleViewTicket = async (ticket) => {
    try {
      const purchaseId = ticket.purchaseId || ticket.id;
      const ticketNumber = await getTicketNumberForPurchase(purchaseId);

      const ticketWithNumber = {
        ...ticket,
        ticket_number: ticketNumber,
        ticketNumber,
        barcode: ticketNumber,
        qrCode: ticketNumber,
      };

      navigate("/viewtickets", {
        state: {
          ticket: ticketWithNumber,
          eventInfo: location.state?.eventInfo || {
            eventName: ticket.eventName,
            eventDate: ticket.eventDate,
            eventLocation: ticket.eventLocation,
            eventImage: ticket.eventImage,
          },
          originalData: location.state,
        },
      });
    } catch (error) {
      console.error("Error fetching ticket number:", error);
      alert("Failed to load the ticket number. Please try again.");
    }
  };

  const handleDownloadTicket = async (ticket) => {
    setGeneratingTicket((prev) => ({ ...prev, [ticket.id]: true }));

    try {
      const purchaseId = ticket.purchaseId || ticket.id;
      const code = String(await getTicketNumberForPurchase(purchaseId)).trim();

      const qrCodeUrl = await QRCodeLib.toDataURL(code, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: "M",
      });

      const canvas = document.createElement("canvas");
      JsBarcode(canvas, code, {
        format: "CODE128",
        displayValue: false,
        margin: 10,
        height: 120,
        width: 3,
      });
      const barcodeUrl = canvas.toDataURL("image/png");

      await generateTicketPDF({
        ticket: {
          ...ticket,
          ticket_number: code,
          barcode: code,
          qrCode: code,
        },
        qrCodeUrl,
        barcodeUrl,
      });
    } catch (error) {
      console.error("Error generating ticket PDF:", error);
      alert("Failed to generate ticket PDF. Please try again.");
    } finally {
      setGeneratingTicket((prev) => ({ ...prev, [ticket.id]: false }));
    }
  };

  const handleDownloadInvoice = async (ticket) => {
    setGeneratingInvoice((prev) => ({ ...prev, [ticket.id]: true }));

    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const invoiceDate = new Date().toLocaleDateString("en-ZA");

      const yellowStart = { r: 239, g: 176, b: 52 };
      const yellowEnd = { r: 243, g: 193, b: 90 };

      doc.setFillColor(248, 250, 252);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      const headerHeight = 40;
      const gradientSteps = 100;

      for (let i = 0; i < gradientSteps; i++) {
        const x = (pageWidth / gradientSteps) * i;
        const width = pageWidth / gradientSteps;

        const ratio = i / (gradientSteps - 1);
        const r = Math.round(yellowStart.r + (yellowEnd.r - yellowStart.r) * ratio);
        const g = Math.round(yellowStart.g + (yellowEnd.g - yellowStart.g) * ratio);
        const b = Math.round(yellowStart.b + (yellowEnd.b - yellowStart.b) * ratio);

        doc.setFillColor(r, g, b);
        doc.rect(x, 0, width + 0.5, headerHeight, "F");
      }

      try {
        const logoX = 10;
        const logoY = 0;
        const logo = await loadImage(logoImage);
        const logoWidth = 60;
        const aspectRatio = logo.width ? logo.height / logo.width : 0.5;
        const logoHeight = Math.max(logoWidth * aspectRatio, 12);

        doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.log("Logo not found, using text fallback");
      }

      doc.setTextColor(33, 33, 33);
      doc.setFontSize(28);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", pageWidth / 2, 25, { align: "center" });

      doc.setTextColor(33, 33, 33);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${invoiceDate}`, pageWidth - 20, 20, { align: "right" });

      let currentY = headerHeight + 20;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", 20, currentY);

      currentY += 10;
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const attendeeName = `${ticket.delegate.name} ${ticket.delegate.surname}`.trim() || "Guest Attendee";
      doc.text(attendeeName, 20, currentY);

      currentY += 6;
      doc.text(ticket.delegate.email, 20, currentY);

      if (ticket.delegate.phone) {
        currentY += 6;
        doc.text(ticket.delegate.phone, 20, currentY);
      }

      currentY += 20;
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(20, currentY - 5, pageWidth - 40, 35, 5, 5, "F");

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Event Details", 25, currentY + 5);

      currentY += 15;
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Event: ${ticket.eventName}`, 25, currentY);

      currentY += 6;
      if (ticket.eventDate) {
        doc.text(`Date: ${formatDate(ticket.eventDate)}`, 25, currentY);
        currentY += 6;
      }

      if (ticket.eventLocation) {
        doc.text(`Location: ${ticket.eventLocation}`, 25, currentY);
      }

      currentY += 25;
      const tableGradientSteps = 100;
      
      for (let i = 0; i < tableGradientSteps; i++) {
        const x = 20 + ((pageWidth - 40) / tableGradientSteps) * i;
        const width = (pageWidth - 40) / tableGradientSteps;
        
        const ratio = i / (tableGradientSteps - 1);
        const r = Math.round(yellowStart.r + (yellowEnd.r - yellowStart.r) * ratio);
        const g = Math.round(yellowStart.g + (yellowEnd.g - yellowStart.g) * ratio);
        const b = Math.round(yellowStart.b + (yellowEnd.b - yellowStart.b) * ratio);
        
        doc.setFillColor(r, g, b);
        doc.rect(x, currentY, width + 0.5, 12, "F");
      }

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Description", 25, currentY + 8);
      doc.text("Qty", pageWidth - 80, currentY + 8, { align: "center" });
      doc.text("Amount", pageWidth - 25, currentY + 8, { align: "right" });

      currentY += 12;
      doc.setFillColor(255, 255, 255);
      doc.rect(20, currentY, pageWidth - 40, 15, "F");
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.rect(20, currentY, pageWidth - 40, 15, "S");

      // ticket.price is the total already paid (flatPrice * 1.03 + 1) — work backwards
      const paidTotal = Number.parseFloat(ticket.price) || 0;
      const flatPrice = paidTotal > 0 ? Math.round(((paidTotal - 1) / 1.03) * 100) / 100 : 0;
      const serviceFee = Math.round((paidTotal - flatPrice) * 100) / 100;
      const totalAmount = paidTotal;

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "normal");
      doc.text(`${ticket.ticketType} - ${ticket.eventName}`, 25, currentY + 8);
      doc.text("1", pageWidth - 80, currentY + 8, { align: "center" });
      doc.text(formatPrice(flatPrice), pageWidth - 25, currentY + 8, { align: "right" });

      currentY += 25;
      const totalSectionY = currentY;

      const paymentCardY = totalSectionY;
      const paymentCardWidth = 80;
      const paymentCardHeight = 35;

      doc.setFillColor(241, 245, 249);
      doc.roundedRect(20, paymentCardY, paymentCardWidth, paymentCardHeight, 5, 5, "F");

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Information", 25, paymentCardY + 5);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Status: Paid`, 25, paymentCardY + 15);
      const paystackReference = ticket.paystackReference || "N/A";
      doc.setFont("helvetica", "bold");
      doc.text("Paystack Reference:", 25, paymentCardY + 21);
      doc.setFont("helvetica", "normal");
      doc.text(paystackReference, 25, paymentCardY + 26);

      doc.setFont("helvetica", "normal");
      doc.text("Flat Ticket Price:", pageWidth - 80, totalSectionY);
      doc.text(formatPrice(flatPrice), pageWidth - 25, totalSectionY, { align: "right" });

      currentY += 8;
      doc.text("Service Fee:", pageWidth - 80, currentY);
      doc.text(formatPrice(serviceFee), pageWidth - 25, currentY, { align: "right" });

      currentY += 8;
      doc.setDrawColor(yellowStart.r, yellowStart.g, yellowStart.b);
      doc.setLineWidth(1);
      doc.line(pageWidth - 100, currentY, pageWidth - 20, currentY);

      currentY += 10;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Total Paid:", pageWidth - 80, currentY);
      doc.text(formatPrice(totalAmount), pageWidth - 25, currentY, { align: "right" });

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("BuzzTicket Terms and Conditions Apply", pageWidth / 2, pageHeight - 15, { align: "center" });

      const sanitizedName = attendeeName.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
      const sanitizedEvent = ticket.eventName.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
      doc.save(`${sanitizedEvent}_${sanitizedName}_Invoice.pdf`);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      alert("Failed to generate invoice PDF. Please try again.");
    } finally {
      setGeneratingInvoice((prev) => ({ ...prev, [ticket.id]: false }));
    }
  };

  const toggleTicketSelection = (ticketId, checked) => {
    if (!selectionMode) {
      return;
    }
    setSelectedTicketIds((prev) => {
      if (checked) {
        if (prev.includes(ticketId)) {
          return prev;
        }
        return [...prev, ticketId];
      }
      return prev.filter((id) => id !== ticketId);
    });
  };

  const handleDownloadCombinedInvoice = async () => {
    if (selectedTicketIds.length < 2) {
      return;
    }

    const selectedTickets = tickets.filter((ticket) => selectedTicketIds.includes(ticket.id));

    if (selectedTickets.length < 2) {
      alert("Select at least two tickets to generate a combined invoice.");
      return;
    }

    setGeneratingCombinedInvoice(true);

    try {
      const { jsPDF } = await import("jspdf");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const invoiceDate = new Date().toLocaleDateString("en-ZA");
      const yellowStart = { r: 239, g: 176, b: 52 };
      const yellowEnd = { r: 243, g: 193, b: 90 };

      doc.setFillColor(248, 250, 252);
      doc.rect(0, 0, pageWidth, pageHeight, "F");

      const headerHeight = 40;
      const gradientSteps = 100;

      for (let i = 0; i < gradientSteps; i++) {
        const x = (pageWidth / gradientSteps) * i;
        const width = pageWidth / gradientSteps;
        const ratio = i / (gradientSteps - 1);
        const r = Math.round(yellowStart.r + (yellowEnd.r - yellowStart.r) * ratio);
        const g = Math.round(yellowStart.g + (yellowEnd.g - yellowStart.g) * ratio);
        const b = Math.round(yellowStart.b + (yellowEnd.b - yellowStart.b) * ratio);

        doc.setFillColor(r, g, b);
        doc.rect(x, 0, width + 0.5, headerHeight, "F");
      }

      try {
        const logoX = 10;
        const logoY = 0;
        const logo = await loadImage(logoImage);
        const logoWidth = 60;
        const aspectRatio = logo.width ? logo.height / logo.width : 0.5;
        const logoHeight = Math.max(logoWidth * aspectRatio, 12);

        doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.log("Logo not found, using text fallback");
      }

      doc.setTextColor(33, 33, 33);
      doc.setFontSize(28);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", pageWidth / 2, 25, { align: "center" });

      doc.setTextColor(33, 33, 33);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${invoiceDate}`, pageWidth - 20, 20, { align: "right" });

      let currentY = headerHeight + 20;
      const primaryDelegate = selectedTickets[0]?.delegate || {};
      const attendeeName = `${primaryDelegate.name || "Guest"} ${primaryDelegate.surname || ""}`.trim() || "Guest Attendee";
      const attendeeEmail = primaryDelegate.email || "No email provided";
      const attendeePhone = primaryDelegate.phone || "";

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", 20, currentY);

      currentY += 10;
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(attendeeName, 20, currentY);

      currentY += 6;
      doc.text(attendeeEmail, 20, currentY);

      if (attendeePhone) {
        currentY += 6;
        doc.text(attendeePhone, 20, currentY);
      }

      if (selectedTickets.length > 1) {
        currentY += 6;
        doc.setFontSize(10);
        doc.setTextColor(107, 114, 128);
        doc.text(`Includes ${selectedTickets.length} tickets`, 20, currentY);
      }

      currentY += 20;
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(20, currentY - 5, pageWidth - 40, 35, 5, 5, "F");

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Event Overview", 25, currentY + 5);

      currentY += 15;
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Events included: ${selectedTickets.length}`, 25, currentY);

      currentY += 25;
      const tableGradientSteps = 100;

      for (let i = 0; i < tableGradientSteps; i++) {
        const x = 20 + ((pageWidth - 40) / tableGradientSteps) * i;
        const width = (pageWidth - 40) / tableGradientSteps;
        const ratio = i / (tableGradientSteps - 1);
        const r = Math.round(yellowStart.r + (yellowEnd.r - yellowStart.r) * ratio);
        const g = Math.round(yellowStart.g + (yellowEnd.g - yellowStart.g) * ratio);
        const b = Math.round(yellowStart.b + (yellowEnd.b - yellowStart.b) * ratio);

        doc.setFillColor(r, g, b);
        doc.rect(x, currentY, width + 0.5, 12, "F");
      }

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Description", 25, currentY + 8);
      doc.text("Qty", pageWidth - 80, currentY + 8, { align: "center" });
      doc.text("Amount", pageWidth - 25, currentY + 8, { align: "right" });

      currentY += 12;
      doc.setFontSize(11);

      const TICKETS_PER_PAGE = 5;
      const tableRowHeight = 16;
      const tableWidth = pageWidth - 40;
      const descriptionX = 25;
      const qtyX = pageWidth - 80;
      const amountX = pageWidth - 25;

      const drawTableHeader = (y) => {
        for (let i = 0; i < tableGradientSteps; i++) {
          const x = 20 + ((pageWidth - 40) / tableGradientSteps) * i;
          const width = (pageWidth - 40) / tableGradientSteps;
          const ratio = i / (tableGradientSteps - 1);
          const r = Math.round(yellowStart.r + (yellowEnd.r - yellowStart.r) * ratio);
          const g = Math.round(yellowStart.g + (yellowEnd.g - yellowStart.g) * ratio);
          const b = Math.round(yellowStart.b + (yellowEnd.b - yellowStart.b) * ratio);
          doc.setFillColor(r, g, b);
          doc.rect(x, y, width + 0.5, 12, "F");
        }
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("Description", descriptionX, y + 8);
        doc.text("Qty", qtyX, y + 8, { align: "center" });
        doc.text("Amount", amountX, y + 8, { align: "right" });
        return y + 12;
      };

      const chunks = [];
      for (let i = 0; i < selectedTickets.length; i += TICKETS_PER_PAGE) {
        chunks.push(selectedTickets.slice(i, i + TICKETS_PER_PAGE));
      }

      chunks.forEach((chunk, chunkIndex) => {
        if (chunkIndex > 0) {
          doc.addPage();
          doc.setFillColor(248, 250, 252);
          doc.rect(0, 0, pageWidth, pageHeight, "F");

          const contHeaderY = 20;
          doc.setTextColor(30, 41, 59);
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(`Tickets (continued — page ${chunkIndex + 1})`, 20, contHeaderY);

          currentY = drawTableHeader(contHeaderY + 12);
          doc.setFontSize(11);
        }

        chunk.forEach((ticket) => {
          doc.setFillColor(255, 255, 255);
          doc.rect(20, currentY, tableWidth, tableRowHeight, "F");
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.5);
          doc.rect(20, currentY, tableWidth, tableRowHeight, "S");

          doc.setTextColor(30, 41, 59);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          const description = `${ticket.ticketType} - ${ticket.eventName}`;
          const reference = ticket.paystackReference ? `Ref: ${ticket.paystackReference}` : "Ref: N/A";
          doc.text(description, descriptionX, currentY + 6);
          doc.setFontSize(9);
          doc.setTextColor(107, 114, 128);
          doc.text(reference, descriptionX, currentY + 12);
          doc.setFontSize(11);
          doc.setTextColor(30, 41, 59);
          const ticketPaid = Number.parseFloat(ticket.price) || 0;
          const ticketFlat = ticketPaid > 0 ? Math.round(((ticketPaid - 1) / 1.03) * 100) / 100 : 0;
          doc.text("1", qtyX, currentY + 8, { align: "center" });
          doc.text(formatPrice(ticketFlat), amountX, currentY + 8, { align: "right" });

          currentY += tableRowHeight + 4;
        });
      });

      // ticket.price is already the total paid per ticket — reverse to get flat prices
      const subtotal = selectedTickets.reduce((sum, ticket) => {
        const paid = Number.parseFloat(ticket.price) || 0;
        const flat = paid > 0 ? Math.round(((paid - 1) / 1.03) * 100) / 100 : 0;
        return sum + flat;
      }, 0);
      const totalAmount = selectedTickets.reduce(
        (sum, ticket) => sum + (Number.parseFloat(ticket.price) || 0),
        0
      );
      const serviceFee = Math.round((totalAmount - subtotal) * 100) / 100;

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);

      currentY += 6;
      doc.text("Subtotal:", pageWidth - 80, currentY);
      doc.text(formatPrice(subtotal), amountX, currentY, { align: "right" });

      currentY += 8;
      doc.text("Service Fee:", pageWidth - 80, currentY);
      doc.text(formatPrice(serviceFee), amountX, currentY, { align: "right" });

      currentY += 8;
      doc.setDrawColor(yellowStart.r, yellowStart.g, yellowStart.b);
      doc.setLineWidth(1);
      doc.line(pageWidth - 100, currentY, pageWidth - 20, currentY);

      currentY += 10;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Total Paid:", pageWidth - 80, currentY);
      doc.text(formatPrice(totalAmount), amountX, currentY, { align: "right" });

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("BuzzTicket Terms and Conditions Apply", pageWidth / 2, pageHeight - 15, { align: "center" });

      const sanitizedName = attendeeName.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
      doc.save(`Combined_${sanitizedName || "Tickets"}_Invoice.pdf`);

      setSelectedTicketIds([]);
      setSelectionMode(false);
    } catch (error) {
      console.error("Error generating combined invoice PDF:", error);
      alert("Failed to generate combined invoice. Please try again.");
    } finally {
      setGeneratingCombinedInvoice(false);
    }
  };

  const handleCombinedButtonClick = () => {
    if (!selectionMode) {
      setSelectionMode(true);
      setSelectedTicketIds([]);
      return;
    }
    if (isCombinedDownloadDisabled) {
      return;
    }
    handleDownloadCombinedInvoice();
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedTicketIds([]);
  };

  const handleLogout = () => {
    logout(navigate);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date not available";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Date not available";
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "N/A") return "R 0.00";

    const numPrice = Number.parseFloat(price);
    return Number.isNaN(numPrice) ? "R 0.00" : `R ${numPrice.toFixed(2)}`;
  };

  const filteredTickets = tickets.filter((ticket) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return true;
    }

    const fieldsToSearch = [
      ticket.eventName,
      ticket.delegate?.name,
      ticket.delegate?.surname,
      ticket.delegate ? `${ticket.delegate.name || ""} ${ticket.delegate.surname || ""}` : null,
      ticket.ticketNumber,
      ticket.ticket_number,
      ticket.id,
      ticket.purchaseId,
    ];

    return fieldsToSearch.some((value) =>
      value && value.toString().toLowerCase().includes(term),
    );
  });

  const selectedTicketsSet = new Set(selectedTicketIds);
  const isCombinedDownloadDisabled =
    selectionMode && (selectedTicketIds.length < 2 || generatingCombinedInvoice);

  const combinedButtonLabel = !selectionMode
    ? "Select Tickets for Combined Invoice"
    : generatingCombinedInvoice
      ? "Generating Combined Invoice..."
      : "Download Combined Invoice";

  return (
    <div className="tickets-page">
      <GlobalHeader
        title="Your Tickets"
        subtitle="View and manage your event tickets"
        backTo="/reviewpurchase"
        logoutTo="/"
        onBack={handleBackToReview}
        onLogout={handleLogout}
      />

      <main className="tickets-main">
        <div className="tickets-toolbar">
          <div className="tickets-toolbar__wrapper">
            <div className="tickets-search">
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onSearch={() => {}}
                placeholder="Search by event, attendee or ticket ID"
              />
            </div>

            <div className="tickets-toolbar__actions">
              {selectionMode && (
                <button
                  type="button"
                  className="tickets-toolbar__button tickets-toolbar__button--secondary"
                  onClick={handleCancelSelection}
                  disabled={generatingCombinedInvoice}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="tickets-toolbar__button"
                onClick={handleCombinedButtonClick}
                disabled={isCombinedDownloadDisabled}
              >
                {combinedButtonLabel}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="tickets-loading">
            <div className="tickets-loading-card">
              <div className="tickets-loading-spinner" />
              <p className="tickets-empty-text">Loading your tickets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="tickets-error">
            <div className="tickets-error-card">
              <p className="tickets-error-text">{error}</p>
            </div>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="tickets-empty">
            <div className="tickets-empty-card">
              <p className="tickets-empty-text">
                {searchTerm
                  ? "No tickets match your search. Try a different search term."
                  : "No tickets found. Please make a purchase to see your tickets here."}
              </p>
            </div>
          </div>
        ) : (
          <div className="tickets-grid">
            {filteredTickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                formatDate={formatDate}
                formatPrice={formatPrice}
                generatingTicket={generatingTicket}
                generatingInvoice={generatingInvoice}
                onViewTicket={handleViewTicket}
                onDownloadTicket={handleDownloadTicket}
                onDownloadInvoice={handleDownloadInvoice}
                selectionEnabled={selectionMode}
                isSelected={selectionMode && selectedTicketsSet.has(ticket.id)}
                onSelectionChange={toggleTicketSelection}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TicketsList;
